import React from 'react'
import { usePostHog } from 'posthog-js/react'

const imgLayoutTable02 = '/icon/layout-table-02.svg'
const imgBubbleChat = '/icon/bubble-chat.svg'
const imgBot = '/icon/bot.svg'
const imgMessageMultiple01 = '/icon/message-multiple-01.svg'
const imgChart01 = '/icon/chart-01.svg'
const imgUserMultiple02 = '/icon/user-multiple-02.svg'
const imgSmartPhone02 = '/icon/smart-phone-02.svg'
const imgRefreshDot = '/icon/refresh-dot.svg'
const imgArtificialIntelligence04 = '/icon/artificial-intelligence-04.svg'
const imgCustomerService02 = '/icon/customer-service-02 2.svg'
const imgTimer02 = '/icon/timer-02.svg'
const imgSymbolSvg = '/icon/Symbol.svg'
const imgCheckmarkCircle03 = '/icon/checkmark-circle-green.svg'
const imgCancelCircle = '/icon/cancel-circle.svg'
const imgAlert02 = '/icon/alert-02.svg'

const features = [
  { icon: imgBubbleChat, label: 'Core Messaging' },
  { icon: imgBot, label: 'Automation & Integrations' },
  { icon: imgMessageMultiple01, label: 'Bulk Messaging' },
  { icon: imgChart01, label: 'Analytics & Reporting' },
  { icon: imgUserMultiple02, label: 'Team Management' },
  { icon: imgSmartPhone02, label: 'Dedicated Mobile Apps' },
  { icon: imgRefreshDot, label: 'Message Sequences' },
  { icon: imgArtificialIntelligence04, label: 'AI-Assisted Replies' },
  { icon: imgCustomerService02, label: 'Customer Support' },
  { icon: imgTimer02, label: 'Setup Time' },
]

const leftStatus = [
  { text: 'Available', type: 'ok' },
  { text: 'Limited', type: 'warn' },
  { text: 'Manual Only', type: 'warn' },
  { text: 'Basic', type: 'alert' },
  { text: 'Single User', type: 'warn' },
  { text: 'No Apps', type: 'warn' },
  { text: 'No Sequences', type: 'warn' },
  { text: 'No AI', type: 'warn' },
  { text: 'Community Only', type: 'alert' },
  { text: 'Manual Setup', type: 'alert' },
]

const rightStatus = [
  'Enhanced',
  '5000+ Apps',
  'Smart Bulk',
  'Advanced',
  'Multi-User',
  'iOS & Android',
  'Automated',
  'AI-Powered',
  'Human Support',
  '5 Minutes',
]

const StatusItem = ({ text, type, size = 'default' }) => {
  const icon =
    type === 'ok'
      ? imgCheckmarkCircle03
      : type === 'warn'
        ? imgCancelCircle
        : imgAlert02
  const iconSize = size === 'small' ? 'h-5 w-5' : 'h-6 w-6'
  const textSize = size === 'small' ? 'text-[14px]' : 'text-[18px]'
  return (
    <div className="flex items-center gap-2">
      <img src={icon} alt="" className={iconSize} />
      <span className={`${textSize} leading-[1.5] tracking-[-0.54px] text-[#1c2329]`}>{text}</span>
    </div>
  )
}

const ComparisonCard = ({
  logo,
  title,
  subtitle,
  price,
  note,
  status,
  cta,
  highlight,
}) => {
  const openEmailModal = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openEmailModal', { detail: { source: 'comparison_cta' } }))
    }
  }

  return (
    <div
      className={`flex h-full flex-1 flex-col rounded-[10px] border ${highlight
          ? 'border-[#24d366] shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)]'
          : 'border-[#f3f3f2]'
        } bg-white px-6 pb-8 pt-10 sm:px-8 sm:pb-10 sm:pt-12 ${highlight ? 'relative overflow-hidden bg-gradient-to-b from-[#c4f5dd] via-[#f5fff9] to-white' : 'overflow-hidden'
        }`}
    >
      <div className="flex flex-col items-center gap-5 text-center sm:gap-6">
        <img src={logo} alt={title} className="h-12 w-12 object-contain" />
        <div className="space-y-1">
          <h3 className="text-[22px] font-semibold tracking-[-0.6px] text-[#1c2329] sm:text-[24px] sm:tracking-[-0.72px]">
            {title}
          </h3>
          <p className="text-[16px] leading-[1.5] text-[#656f68] sm:text-[18px]">{subtitle}</p>
        </div>
        <div className="h-px w-full bg-[#e7e7e7]" />
        <div className="flex flex-col gap-4">
          {status.map((item) =>
            typeof item === 'string' ? (
              <div key={item} className="flex items-center gap-2">
                <img src={imgCheckmarkCircle03} alt="" className="h-6 w-6" />
                <span className="text-[18px] leading-[1.5] tracking-[-0.54px] text-[#1c2329]">{item}</span>
              </div>
            ) : (
              <StatusItem key={item.text} {...item} />
            )
          )}
        </div>
        {cta && (
          <button
            onClick={openEmailModal}
            className="w-full rounded-full bg-[#1c2329] px-8 py-3 text-[15px] font-semibold tracking-[-0.45px] text-white shadow-[0px_4px_8px_rgba(36,196,211,0.1)] sm:px-10 sm:py-4 sm:text-[16px] sm:tracking-[-0.48px]"
          >
            {cta}
          </button>
        )}
      </div>
    </div>
  )
}

const Comparison = () => {
  const posthog = usePostHog()

  const openEmailModal = () => {
    posthog?.capture('comparison_cta_clicked', { source: 'comparison_section' })
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openEmailModal', { detail: { source: 'comparison_cta' } }))
    }
  }

  return (
    <section className="bg-white px-4 py-10 sm:px-6 sm:py-12 lg:px-[100px] lg:py-16">
      <div className="mx-auto max-w-[1240px] space-y-8 sm:space-y-10">
        <div className="flex items-center gap-2 rounded-full bg-[#fafafa] px-4 py-2 text-xs font-medium uppercase tracking-[0.28px] text-[#1c2329] shadow-[inset_0px_1px_1px_rgba(26,32,50,0.1),inset_0px_1px_2px_rgba(26,32,50,0.05)] w-fit font-mono sm:text-[14px]">
          <img src={imgLayoutTable02} alt="" className="h-6 w-6" />
          <span>Comparison</span>
        </div>
        <div className="space-y-3">
          <h2 className="text-[30px] font-semibold leading-[1.2] tracking-[-0.9px] text-[#1c2329] sm:text-[42px] sm:leading-[1.15] lg:text-[56px] lg:leading-[64px] lg:tracking-[-1.68px]">
            Complete features comparison
          </h2>
          <p className="text-[16px] leading-[1.7] tracking-[-0.48px] text-[#656f68] sm:text-[18px] sm:tracking-[-0.54px]">
            Notifyer transforms your WhatsApp Business experience with powerful automation, integrations, and team
            management features.
          </p>
        </div>

        {/* Mobile Card Layout */}
        <div className="block md:hidden space-y-4">
          {features.map((feat, index) => (
            <div
              key={feat.label}
              className="rounded-[12px] border border-[#f2f3f3] bg-white p-4 space-y-4"
            >
              {/* Feature Header */}
              <div className="flex items-center gap-3 pb-3 border-b border-[#f2f3f3]">
                <img src={feat.icon} alt="" className="h-6 w-6 flex-shrink-0" />
                <span className="text-[16px] font-medium tracking-[-0.48px] text-[#1c2329]">{feat.label}</span>
              </div>

              {/* Comparison Cards */}
              <div className="grid grid-cols-2 gap-3">
                {/* WhatsApp Business API */}
                <div className="flex flex-col gap-2 p-3 rounded-[8px] bg-[#fafaf9] border border-[#f2f3f3]">
                  <div className="flex items-center gap-2 mb-1">
                    <img src={imgSymbolSvg} alt="WhatsApp Business API" className="h-5 w-5 object-contain" />
                    <span className="text-[12px] font-semibold text-[#1c2329]">WhatsApp API</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <StatusItem text={leftStatus[index].text} type={leftStatus[index].type} size="small" />
                  </div>
                </div>

                {/* Notifyer */}
                <div className="flex flex-col gap-2 p-3 rounded-[8px] bg-gradient-to-b from-[#c4f5dd] via-[#f5fff9] to-white border border-[#24d366]">
                  <div className="flex items-center gap-2 mb-1">
                    <img src="/notifyer-icon.svg" alt="Notifyer" className="h-5 w-5 object-contain" />
                    <span className="text-[12px] font-semibold text-[#1c2329]">Notifyer</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {typeof rightStatus[index] === 'string' ? (
                      <div className="flex items-center gap-1.5">
                        <img src={imgCheckmarkCircle03} alt="" className="h-5 w-5" />
                        <span className="text-[14px] leading-[1.4] tracking-[-0.42px] text-[#1c2329]">
                          {rightStatus[index]}
                        </span>
                      </div>
                    ) : (
                      <StatusItem text={rightStatus[index].text} type={rightStatus[index].type} size="small" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Mobile CTA */}
          <div className="pt-4">
            <button
              onClick={openEmailModal}
              className="w-full rounded-full bg-[#1c2329] px-8 py-3 text-[15px] font-semibold tracking-[-0.45px] text-white shadow-[0px_4px_8px_rgba(36,196,211,0.1)]"
            >
              Get Started with Notifyer
            </button>
          </div>
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden md:block overflow-x-auto">
          <div className="min-w-[800px] rounded-[16px] border border-[#f2f3f3] bg-[#fafaf9] p-4 sm:p-6">
            {/* Header Row */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 pb-4 mb-4 border-b border-[#f2f3f3]">
              <div className="flex items-center">
                <p className="text-[17px] font-medium tracking-[-0.51px] text-[#1c2329] sm:text-[18px] sm:tracking-[-0.54px]">
                  Essential Features
                </p>
              </div>
              <div className="flex flex-col items-center gap-2 text-center min-w-[200px] p-4">
                <img src={imgSymbolSvg} alt="WhatsApp Business API" className="h-10 w-10 object-contain" />
                <div>
                  <h3 className="text-[18px] font-semibold tracking-[-0.54px] text-[#1c2329]">WhatsApp Business API</h3>
                  <p className="text-[14px] text-[#656f68]">Basic Platform</p>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 text-center min-w-[200px] relative overflow-hidden rounded-[10px] bg-gradient-to-b from-[#c4f5dd] via-[#f5fff9] to-white p-4 border border-[#24d366]">
                <img src="/notifyer-icon.svg" alt="Notifyer" className="h-10 w-10 object-contain relative z-10" />
                <div className="relative z-10">
                  <h3 className="text-[18px] font-semibold tracking-[-0.54px] text-[#1c2329]">Notifyer</h3>
                  <p className="text-[14px] text-[#656f68]">Enhanced platform</p>
                </div>
              </div>
            </div>

            {/* Feature Rows */}
            <div className="space-y-4">
              {features.map((feat, index) => (
                <div
                  key={feat.label}
                  className="grid grid-cols-[1fr_auto_auto] gap-4 items-center py-3 min-h-[48px]"
                >
                  {/* Feature Name */}
                  <div className="flex items-center gap-3 text-[16px] tracking-[-0.48px] text-[#656f68] sm:text-[18px] sm:tracking-[-0.54px]">
                    <img src={feat.icon} alt="" className="h-6 w-6 flex-shrink-0" />
                    <span>{feat.label}</span>
                  </div>

                  {/* WhatsApp Business API Status */}
                  <div className="flex justify-start items-center min-w-[200px] h-full">
                    <StatusItem text={leftStatus[index].text} type={leftStatus[index].type} />
                  </div>

                  {/* Notifyer Status */}
                  <div className="flex justify-start items-center min-w-[200px] h-full">
                    {typeof rightStatus[index] === 'string' ? (
                      <div className="flex items-center gap-2">
                        <img src={imgCheckmarkCircle03} alt="" className="h-6 w-6" />
                        <span className="text-[18px] leading-[1.5] tracking-[-0.54px] text-[#1c2329]">
                          {rightStatus[index]}
                        </span>
                      </div>
                    ) : (
                      <StatusItem text={rightStatus[index].text} type={rightStatus[index].type} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Row */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 pt-6 mt-6 border-t border-[#f2f3f3]">
              <div></div>
              <div className="min-w-[200px]"></div>
              <div className="flex justify-start min-w-[200px]">
                <button
                  onClick={openEmailModal}
                  className="w-full rounded-full bg-[#1c2329] px-8 py-3 text-[15px] font-semibold tracking-[-0.45px] text-white shadow-[0px_4px_8px_rgba(36,196,211,0.1)] sm:px-10 sm:py-4 sm:text-[16px] sm:tracking-[-0.48px]"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Comparison

